import React, { useState, useEffect, useRef } from "react";
import {
  MinusCircleIcon,
  PlusCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  getBroadcastGroupsData,
  getFilteredBroadcastGroupData,
} from "@services/API/Requests/BroadcastGroupRequests";
import { BROADCASTGROUP } from "@types/broadcastGroup";
import { error_message } from "@constants/Errors";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import LoadingIcon from "@common/LoadingIcon";
import { APP_NAMES } from "@constants/Enums";

interface BroadcastGroupModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onSelectGroup: (group: BROADCASTGROUP) => void;
}

const BroadcastGroupModal: React.FC<BroadcastGroupModalProps> = ({
  isOpen,
  onSelectGroup,
  onRequestClose,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [groups, setGroups] = useState<BROADCASTGROUP[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<BROADCASTGROUP[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  setFilteredGroups;

  const [expandedNodes, setExpandedNodes] = useState<{
    [key: string]: boolean;
  }>({});

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalGroups, setTotalGroups] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);

  const totalPages: number = Math.ceil(totalGroups / itemsPerPage);
  const availableApps = useSelector((state) => state.apps.availableApps);

  const hasYobelyLicense = availableApps.includes(APP_NAMES.YOBELY);

  useEffect(() => {
    const fetchData = async () => {
      setExpandedNodes({});
      try {
        setLoading(true);
        await getBroadcastGroupsData(
          setGroups,
          setLoading,
          setError,
          currentPage,
          itemsPerPage,
          setTotalGroups,
        );
        setSearchTerm("");
      } catch (err) {
        console.error(err);
        setError(error_message.yobely.not_found);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen && hasYobelyLicense) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredGroups(groups);
    }
  }, [searchTerm, groups]);

  const doSearch = async () => {
    try {
      setLoading(true);
      if (!searchTerm) {
        setFilteredGroups(groups);
      } else {
        const query = `name=${searchTerm}`;
        await getFilteredBroadcastGroupData(
          setFilteredGroups,
          setError,
          setLoading,
          query,
        );
      }
    } catch (error) {
      setError(error_message.yobely.search);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const toggleExpand = (id) => {
    setExpandedNodes((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const buildGroupTree: (groups: any) => never[] = (groups) => {
    const sortedGroups: any = groups.sort((a, b) =>
      a.code.localeCompare(b.code),
    );

    const map = {};
    const roots = [];

    sortedGroups.forEach((group) => {
      map[group.id] = { ...group, children: [] };
    });

    sortedGroups.forEach((group) => {
      if (group.parent === null) {
        roots.push(map[group.id]);
      } else {
        if (map[group.parent]) {
          map[group.parent].children.push(map[group.id]);
        }
      }
    });

    const sortChildren = (nodes) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          node.children.sort((a, b) => a.code.localeCompare(b.code));
          sortChildren(node.children);
        }
      });
    };

    sortChildren(roots);

    return roots;
  };

  const GroupTree = ({ groups }) => {
    const displayTree = (nodes) => {
      return (
        <ul>
          {nodes.map((node) => (
            <li key={node.id} className="node-item ml-2">
              <div className="flex justify-between items-center">
                {node.children.length !== 0 ? (
                  <div
                    className="flex justify-normal items-center"
                    onClick={() => toggleExpand(node.id)}
                  >
                    {expandedNodes[node.id] ? (
                      <MinusCircleIcon className="mr-5 w-5 h-5 text-mayo-link-text hover:opacity-50" />
                    ) : (
                      <PlusCircleIcon className="mr-5 w-5 h-5 text-mayo-link-text hover:opacity-50" />
                    )}
                    <div className="underline hover:opacity-50 cursor-pointer label-light-blue-xl">
                      [{node.code}] {node.name}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-normal items-center">
                    <div className="label-light-blue-xl">
                      [{node.code}] {node.name}
                    </div>
                  </div>
                )}

                <div>
                  <button
                    className="button-small-white ml-2 items-end"
                    onClick={() => {
                      onSelectGroup(node);
                      onRequestClose();
                    }}
                  >
                    選択
                  </button>
                </div>
              </div>
              <div className="divider mt-2"></div>
              {node.children &&
                node.children.length > 0 &&
                expandedNodes[node.id] && (
                  <div className="ml-10 mt-2">{displayTree(node.children)}</div>
                )}
            </li>
          ))}
        </ul>
      );
    };

    const treeData = buildGroupTree(groups);

    return <div>{displayTree(treeData)}</div>;
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center backdrop-brightness-50 z-[1000]">
      <div className="bg-white border border-text-field-border rounded-lg w-1/2 max-h-[60vh] flex flex-col overflow-hidden">
        <div className="mayo-card-header p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">一斉発信グループ選択</h2>
          <button onClick={onRequestClose} className="text-xl hover:opacity-50">
            <XCircleIcon className="w-5 h-5 text-gray-600 hover:text-primary" />
          </button>
        </div>

        <div className="mayo-card-body-modal overflow-y-auto p-4 flex-grow">
          {loading ? (
            <div className="flex justify-center items-center mt-10">
              <LoadingIcon />
            </div>
          ) : error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : filteredGroups?.length === 0 ? (
            <p className="text-center text-xl text-black">
              {error_message.yobely.not_found}
              <br />
              {!hasYobelyLicense && "YOBELYライセンスが必要です。"}
            </p>
          ) : (
            <div className="w-full">
              <GroupTree
                groups={filteredGroups}
                expandedNodes={expandedNodes}
                toggleExpand={toggleExpand}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 p-4">
          <button onClick={onRequestClose} className="button-small-white">
            キャンセル
          </button>
          {hasYobelyLicense && (
            <Link to="/YOBELY/broadcast_groups/create/Master">
              <button className="button-small-white">グループ登録に遷移</button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default BroadcastGroupModal;
